import styled from "@emotion/styled";
import { useEffect, useState } from "react";
import { Helmet } from "react-helmet-async";
import dayjs from "dayjs";
import { convertTravelInsuranceRange, convertTravelInsuranceRangeBack, i18n, mediaQuery, navigate, TravelInsuranceRangeEnum, useChangeTravelInsuranceRange, useGeneralSoContext, useGetCalculation, useGetSo, useSessionStorage } from "src/common";
import { BrandCalculationDto, Coverage, TravelDataType } from "src/common/apiGraphql/types/types";
import {
  AdditionalCoverageForm,
  ArrowDownIcon,
  ArrowLeftIcon,
  BaseCoverageForm,
  BottomDrawer,
  Button,
  CalculationsError,
  ContainerComponent,
  DesktopContainer,
  Flex,
  Loader,
  MobileContainer,
  OfferCard,
  PartnerBanner,
  Stepper,
  Text
} from "src/components";
import { baseWidths, colors } from "src/theme";
import { AppRoutes } from "./_router/app.routes";
import { useParams } from "react-router-dom";


const ContentContainer = styled.div({
  [mediaQuery('laptop')]: {
    display: 'flex',
    width: 'calc(100% - 600px)',
    justifyContent: 'flex-end',
  },
})

const Content = styled(Flex)({
  [mediaQuery('mobile')]: {
    width: `${baseWidths.mobile}px`
  },
})

const MobileCoveragesContainer = styled.div({
  position: 'fixed',
  bottom: 0,
  left: 0,
  width: '100%',
  height: 'auto',
  backgroundColor: colors.primary1,
  zIndex: 1000,
  padding: '16px',
})

const StatusRegion = styled.div({
  position: 'absolute',
  width: 1,
  height: 1,
  padding: 0,
  margin: -1,
  overflow: 'hidden',
  clip: 'rect(0 0 0 0)',
  whiteSpace: 'nowrap',
  border: 0,
})

const getOffersStatusMessage = (loading: boolean, count?: number): string => {
  if (loading) return 'Učitavanje ponuda…';
  if (count === undefined) return '';
  if (count === 1) return 'Pronađena 1 ponuda';
  const lastDigit = count % 10;
  const lastTwoDigits = count % 100;
  const isFew = lastDigit >= 2 && lastDigit <= 4 && (lastTwoDigits < 12 || lastTwoDigits > 14);
  return `Pronađeno ${count} ${isFew ? 'ponude' : 'ponuda'}`;
}

const OffersPage = () => {
  const { so, refetch } = useGetSo();
  const { soId, setSoInitialData, setSoId } = useGeneralSoContext();
  const { fetchCalculations, calculations, loading, refetch: refetchCalculations } = useGetCalculation();
  const { setValue: setSelectedCoveragesStorage, getValue: getSelectedCoveragesStorage } = useSessionStorage<Array<{ code: string; variant: string }>>('selectedCoverages');
  const [selectedCoverages, setSelectedCoverages] = useState<Array<{ code: string; variant: string }>>(() => {
    return getSelectedCoveragesStorage() || [];
  });
  const [isDrawerOpen, setIsDrawerOpen] = useState(false);
  const [changeTravelInsuranceRange] = useChangeTravelInsuranceRange();
  // Calculations are undefined while reloading; keeping the last coverages stops the form from
  // unmounting on every change, which would drop keyboard focus
  const [additionalCoverages, setAdditionalCoverages] = useState<Coverage[]>([]);

  useEffect(() => {
    if (calculations?.additionalCoverages) {
      setAdditionalCoverages(calculations.additionalCoverages as Coverage[]);
    }
  }, [calculations?.additionalCoverages]);

  const { soId: soIdFromUrl } = useParams();

  useEffect(() => {
    if (so) {
      refetch();
    }
  }, []);

  useEffect(() => {
    if (soIdFromUrl) {
      setSoId(soIdFromUrl);
    }
  }, [soIdFromUrl]);

  useEffect(() => {
    if (so) {
      fetchCalculations(so.productTypeSpecific?.travelData as TravelDataType, selectedCoverages);
      setSoInitialData(so.productTypeSpecific?.travelData as TravelDataType);
    }
    if (so?.productTypeSpecific?.travelData?.insuranceStartDate) {
      const startDate = dayjs(so.productTypeSpecific.travelData.insuranceStartDate);
      const today = dayjs().startOf('day');
      if (startDate.isBefore(today) || startDate.isSame(today, 'day')) {
        navigate(AppRoutes.WEBSHOP.STEP_1);
      }
    }
  }, [so]);

  const handleCoverageChange = (coverages: Array<{ code: string; variant: string }>) => {
    setSelectedCoverages(coverages);
    setSelectedCoveragesStorage(coverages);
  };

  useEffect(() => {
    if (so) {
      fetchCalculations(
        so.productTypeSpecific?.travelData as TravelDataType,
        selectedCoverages
      );
    }
  }, [selectedCoverages]);

  if (!soId) {
    setSoInitialData(undefined);
    navigate(AppRoutes.WEBSHOP.STEP_1);
    return null;
  }

  const handleInsuranceRangeChange = (value: TravelInsuranceRangeEnum) => {
    changeTravelInsuranceRange(convertTravelInsuranceRange(value));
  };

  if (calculations && calculations.calculations && calculations.calculations.length === 0) {
    return (
    <ContainerComponent>
      <CalculationsError onRetry={() => refetchCalculations()} />
    </ContainerComponent>
    )
  }

  return (
    <>
      <Helmet>
        <title>Ponude | Putno osiguranje - kompare.hr</title>
      </Helmet>
      <div>
        <Flex gap={64}>
          <ContentContainer>
            <Content vertical gap={12}>
              <Flex vertical gap={32}>
                <Stepper currentStep={3} totalSteps={5} />
                <Flex vertical gap={16}>
                  <Flex vertical>
                    <Text as="h1" type="xxLarge" bold>
                      {i18n.t(`pages.offers.title`)}
                    </Text>
                    <Text type="small" color={colors.text4}>
                      {i18n.t("pages.offers.subtitle")}
                    </Text>
                  </Flex>
                  <PartnerBanner source={so?.source} />

                  <StatusRegion role="status">
                    {getOffersStatusMessage(loading, calculations?.calculations?.length)}
                  </StatusRegion>
                  <div aria-busy={loading}>
                  <Flex vertical gap={16}>
                    {calculations?.calculations?.map((calculation, index) => (
                      <Flex vertical gap={16} key={index}>
                        <OfferCard
                          offer={calculation as BrandCalculationDto}
                          insuranceType={so?.productTypeSpecific?.travelData?.insuranceType}
                          insuranceRange={so?.productTypeSpecific?.travelData?.insuranceRange}
                        />
                      </Flex>
                    ))}
                    {loading && <Loader />}
                  </Flex>
                  </div>
                  <Flex>
                    <Button iconPosition="left" btnType="textBlack" text={i18n.t("components.buttons.back")} onClick={() => navigate(AppRoutes.WEBSHOP.STEP_2)}>
                      <ArrowLeftIcon color={colors.black} />
                    </Button>
                  </Flex>
                </Flex>
              </Flex>
            </Content>
          </ContentContainer>
          <DesktopContainer>
            {so?.productTypeSpecific?.travelData?.insuranceRange &&
              <BaseCoverageForm
                insuranceRange={convertTravelInsuranceRangeBack(so?.productTypeSpecific?.travelData?.insuranceRange)}
                onInsuranceRangeChange={handleInsuranceRangeChange}
              />
            }
            {additionalCoverages.length > 0 && (
              <AdditionalCoverageForm
                coverages={additionalCoverages}
                selectedCoverages={selectedCoverages}
                onCoverageChange={handleCoverageChange}
              />
            )}
          </DesktopContainer>
        </Flex>
        <MobileContainer>
          <MobileCoveragesContainer>
            <Flex vertical gap={16}>
              <Text type="small" bold color={colors.white}>
                {i18n.t(`components.forms.additionalCoverage.subtitle`)}
              </Text>
              <Button iconPosition="left" btnType="primary" text={i18n.t("pages.offers.additionalCoverages")} onClick={() => setIsDrawerOpen(true)} >
                <ArrowDownIcon color={colors.white} />
              </Button>
            </Flex>
          </MobileCoveragesContainer>
        </MobileContainer>
      </div>
      <MobileContainer>
        <BottomDrawer open={isDrawerOpen} onClose={() => setIsDrawerOpen(false)} backgroundColor={colors.primary1} closeIconColor={colors.white}>
          {so?.productTypeSpecific?.travelData?.insuranceRange &&
            <BaseCoverageForm
              insuranceRange={convertTravelInsuranceRangeBack(so?.productTypeSpecific?.travelData?.insuranceRange)}
              onInsuranceRangeChange={handleInsuranceRangeChange}
            />
          }
          {additionalCoverages.length > 0 && (
            <AdditionalCoverageForm
              coverages={additionalCoverages}
              selectedCoverages={selectedCoverages}
              onCoverageChange={handleCoverageChange}
            />
          )}
        </BottomDrawer>
      </MobileContainer>
    </>
  );
};

export default OffersPage;